import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { apiClient, SalaryDetail, User } from '../lib/api'
import { useLiff } from '../hooks/useLiff'

export default function SalaryPage() {
  const { accessToken, isReady } = useLiff()
  const [user, setUser] = useState<User | null>(null)
  const [userLoading, setUserLoading] = useState(true)
  const [salary, setSalary] = useState<SalaryDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedMonth, setSelectedMonth] = useState('')

  // 現在の月をデフォルトに設定
  const getCurrentMonth = () => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  }

  // 月のオプション生成（過去6ヶ月分）
  const generateMonthOptions = () => {
    const options = []
    const now = new Date()
    
    for (let i = 0; i < 6; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const monthStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      const displayStr = date.toLocaleDateString('ja-JP', { year: 'numeric', month: 'long' })
      options.push({ value: monthStr, label: displayStr })
    }
    
    return options
  }

  const loadSalary = async (month: string) => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiClient.getSalary(month)
      if (response.success) {
        setSalary(response.salary)
      } else {
        throw new Error(response.error || '給与データの取得に失敗しました')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました')
      setSalary(null)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('ja-JP', { 
      month: 'short', 
      day: 'numeric',
      weekday: 'short'
    })
  }

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString('ja-JP') + '円'
  }

  useEffect(() => {
    const currentMonth = getCurrentMonth()
    setSelectedMonth(currentMonth)
  }, [])

  const loadUser = async () => {
    try {
      setUserLoading(true)
      const response = await apiClient.getMe()
      setUser(response.user)
    } catch (error) {
      console.error('Failed to load user:', error)
    } finally {
      setUserLoading(false)
    }
  }

  useEffect(() => {
    if (isReady && accessToken) {
      apiClient.setAccessToken(accessToken)
      loadUser()
    }
  }, [isReady, accessToken])

  useEffect(() => {
    if (user && selectedMonth) {
      loadSalary(selectedMonth)
    }
  }, [user, selectedMonth])

  if (!isReady || userLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
      {/* ヘッダー */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="flex items-center justify-between px-4 py-4">
          <div className="flex items-center space-x-3">
            <Link to="/" className="text-purple-500 text-xl">←</Link>
            <div>
              <h1 className="text-lg font-bold text-gray-900">給与明細</h1>
            </div>
          </div>
          {user?.role === 'admin' && (
            <Link
              to="/salary-admin"
              className="bg-purple-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-600"
            >
              管理画面
            </Link>
          )}
        </div>
      </header>

      <main className="p-4 pb-20">
        {/* 月選択 */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-6">
          <label htmlFor="month" className="block text-sm font-medium text-gray-700 mb-2">
            対象月を選択
          </label>
          <select
            id="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">月を選択してください</option>
            {generateMonthOptions().map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
          </div>
        ) : salary ? (
          <div className="space-y-6">
            {/* 給与サマリー */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
              <div className="flex items-center mb-4">
                <div className="text-2xl mr-3">💰</div>
                <h2 className="text-xl font-bold text-gray-900">給与サマリー</h2>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-600">時給</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {formatCurrency(salary.hourlyWage)}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">勤務時間</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {salary.totalWorkHours}時間
                  </div>
                </div>
              </div>
              
              <div className="border-t border-gray-200 mt-4 pt-4">
                <div className="text-center">
                  <div className="text-sm text-gray-600 mb-1">支給額</div>
                  <div className="text-3xl font-bold text-purple-600">
                    {formatCurrency(salary.totalAmount)}
                  </div>
                </div>
              </div>
            </div>

            {/* 日別内訳 */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
              <div className="flex items-center mb-4">
                <div className="text-2xl mr-3">📅</div>
                <h2 className="text-xl font-bold text-gray-900">日別勤務内訳</h2>
              </div>

              {salary.dailyDetails.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-4xl mb-4">😴</div>
                  <p className="text-gray-600">この月の勤務データがありません</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {salary.dailyDetails.map((detail, index) => (
                    <div key={index} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
                      <div>
                        <div className="font-medium text-gray-900">
                          {formatDate(detail.date)}
                        </div>
                        <div className="text-sm text-gray-600">
                          {detail.startTime} - {detail.endTime}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-gray-900">
                          {formatCurrency(detail.amount)}
                        </div>
                        <div className="text-sm text-gray-600">
                          {detail.workHours}時間
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* スタッフ情報 */}
            <div className="bg-gray-50 rounded-xl border border-gray-100 p-4">
              <div className="text-sm text-gray-600 text-center">
                {salary.employeeName} さんの給与明細
              </div>
            </div>
          </div>
        ) : selectedMonth && (
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 text-center">
            <div className="text-4xl mb-4">🔍</div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">データがありません</h2>
            <p className="text-gray-600">
              選択された月の給与データが見つかりませんでした
            </p>
          </div>
        )}
      </main>
    </div>
  )
}